import React, { useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';

import type { ArrayElement } from '../../../util/types';
import { CourseGraphQL } from '../../../api/content';
import {
  addTopicsToCurriculum,
  getMyUnendedClassrooms,
  removeTopicsFromCurriculum,
} from '../../../api/educator';

const MY_UNENDED_CLASSROOMS_QUERY_KEY = [
  'educator',
  'me',
  'unendedCurriculums',
] as const;

export interface Props {
  topic: ArrayElement<CourseGraphQL['topicsEntities']>;
  onError: (err: Error) => void;
}

function DefaultBox({ children }: { children: React.ReactNode }) {
  return (
    <Box
      alignItems="center"
      justifyContent="center"
      display="flex"
      flexBasis="row"
      padding={1}
      minWidth={300}
      minHeight={50}
    >
      {children}
    </Box>
  );
}

function MiniCurriculumTopicEditorContent({ topic, onError }: Props) {
  const {
    data,
    isLoading,
    refetch: refechUnendedClassrooms,
  } = useQuery({
    queryKey: MY_UNENDED_CLASSROOMS_QUERY_KEY,
    queryFn: async () => await getMyUnendedClassrooms(true),
    onError: err => {
      console.error(err);
      onError(
        new Error(
          'We were unable to retrieve your unended classrooms. If this problem persists, please email support@picardylearning.com.',
        ),
      );
    },
  });

  const { isLoading: isPersisting, mutate: updateTopicsForCurriculum } =
    useMutation({
      mutationKey: ['educator', 'me', 'updateCurriculums'] as const,
      mutationFn: async ({
        checked,
        curriculumId,
      }: {
        checked: boolean;
        curriculumId: string;
      }) => {
        if (checked) {
          await addTopicsToCurriculum(curriculumId, [topic._id]);
        } else {
          await removeTopicsFromCurriculum(curriculumId, [topic._id]);
        }
        await refechUnendedClassrooms();
      },
      onError: err => {
        console.error(err);
        onError(
          new Error(
            'Something went wrong while updating your curriculum. Please try again or reach to support@picardylearning.com.',
          ),
        );
      },
    });

  const selectedCurriculumMap: Record<string, boolean> = useMemo(
    () =>
      data?.reduce(
        (accum, classroom) =>
          Object.assign(accum, {
            [classroom._id]: classroom.curriculum.topics.includes(topic._id),
          }),
        {},
      ) ?? {},
    [data, topic],
  );

  if (isLoading) return <CircularProgress />;

  if (data && data.length === 0)
    return (
      <Typography color="secondary">
        You don't have any active classrooms right now. Head to{' '}
        <Link href="/portal" target="_blank">
          My Classrooms
        </Link>{' '}
        to get started.
      </Typography>
    );

  return (
    <Box width="100%">
      <FormControl component="fieldset">
        <FormLabel component="legend">Add to classrooms</FormLabel>
        {data?.map(classroom => (
          <FormGroup key={topic._id}>
            <FormControlLabel
              disabled={isPersisting}
              control={
                <Checkbox
                  disabled={isPersisting}
                  size="small"
                  checked={selectedCurriculumMap[classroom._id]}
                  onChange={(_ev, checked) =>
                    updateTopicsForCurriculum({
                      curriculumId: classroom.curriculum._id,
                      checked,
                    })
                  }
                />
              }
              label={classroom.name}
            />
          </FormGroup>
        ))}
      </FormControl>
    </Box>
  );
}

export default function MiniCurriculumTopicEditor({ topic, onError }: Props) {
  return (
    <DefaultBox>
      <MiniCurriculumTopicEditorContent topic={topic} onError={onError} />
    </DefaultBox>
  );
}
